import React, { useState, useId } from 'react';
import { useAuth } from '../Login/AuthContext';
import SettingsSelect from './SettingsSelect';
import WorksheetStylePreview from './WorksheetStylePreview';
import SubjectsLibrary from '../Subjects/Subject.js';
import promptSuggestions from './promptSuggestions.js';
import { useRegion } from '../../customhooks/useRegion';
import './CustomPrompt.css';

function pickRandom3(list) {
  const shuffled = [...list].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 3);
}

const PROMPT_INSTRUCTIONS = `
Please list only the questions without any additional text or explanations.
Each question should include a corresponding answer listed in a separate section titled 'Answers', directly following the list of questions.
Ensure that each question uses inline KaTeX with the dollar signs for rendering LaTeX in web environments.
Any questions involving multiplication should explicitly use the multiplication sign and not the dot.
Any questions involving money make sure to use the "£" and "$" signs accordingly for relevant curriculums if mentioned.`;
const MAX_USER_PROMPT_CHARS = 2000;

function buildEnrichedPrompt(userText, region, difficulty, selectedSubjects) {
  const curriculum = region === 'US' ? 'US Common Core' : 'UK National Curriculum';
  const context = [];
  context.push(`following the ${curriculum}`);
  if (difficulty) context.push(`at ${difficulty} difficulty`);
  if (selectedSubjects.length > 0) context.push(`covering the following topics: ${selectedSubjects.join(', ')}`);
  return [userText.trim(), `(${context.join(', ')})`, PROMPT_INSTRUCTIONS].join('\n');
}

function validatePrompt(text) {
  const trimmed = text.trim();
  if (!trimmed) return 'Please describe your worksheet.';
  if (trimmed.length < 8) return 'Please write a more descriptive prompt.';
  if (trimmed.length > MAX_USER_PROMPT_CHARS)
    return `Please keep your prompt under ${MAX_USER_PROMPT_CHARS.toLocaleString()} characters.`;
  if (/^[^\p{L}]+$/u.test(trimmed)) return 'Your prompt should contain some words.';
  const words = trimmed.split(/\s+/).filter(w => /\p{L}/u.test(w));
  if (words.length < 2) return 'Please write at least a couple of words.';
  if (/(.)\1{4,}/.test(trimmed))
    return "That doesn't look like a valid prompt — please try again.";
  if (/qwerty|asdfgh|zxcvbn|asdf|zxcv|hjkl|bnm,/i.test(trimmed))
    return "That doesn't look like a valid prompt — please try again.";
  return null;
}

export default function CustomPrompt({
  onGenerate,
  loading,
  initialText = '',
  initialRegion = null,
  initialDifficulty = null,
  initialTopics = [],
  initialSettings = {},
  showSuggestions = true,
  compact = false,
}) {
  const styleGroupId = useId();
  // Positions are picked once; the region decides which list they're read from.
  const [suggestionPicks] = useState(() => pickRandom3([...promptSuggestions.UK.keys()]));
  const [userText, setUserText] = useState(initialText);
  const [region, setRegion] = useRegion(initialRegion);
  const suggestions = suggestionPicks.map((i) => (promptSuggestions[region] || promptSuggestions.UK)[i]);
  const [difficulty, setDifficulty] = useState(initialDifficulty);
  const [selectedSubjects, setSelectedSubjects] = useState(initialTopics);
  const [subjectsOpen, setSubjectsOpen] = useState(false);
  const [settings, setSettings] = useState({ style: 'clean', language: 'auto', yearGroup: '', ...initialSettings, readingLevel: initialSettings.readingLevel === 'advanced' ? 'adult' : (initialSettings.readingLevel || 'match') });
  const changeSetting = (key, value) => setSettings(previous => ({ ...previous, [key]: value }));
  const [error, setError] = useState('');
  const quotaBlocked = useAuth()?.aiAllowance?.usage?.remaining === 0;

  const handleGenerate = () => {
    if (quotaBlocked || loading) return;
    const err = validatePrompt(userText);
    if (err) { setError(err); return; }
    setError('');
    onGenerate({
      raw: userText.trim(),
      enriched: buildEnrichedPrompt(userText, region, difficulty, selectedSubjects),
      region,
      difficulty,
      topics: selectedSubjects,
      style: settings.style, language: settings.language, readingLevel: settings.readingLevel,
      yearGroup: settings.yearGroup || null,
    });
  };

  const handleRemovePill = (pill) => {
    if (pill.type === 'difficulty') setDifficulty(null);
    else if (pill.type === 'subject') setSelectedSubjects(prev => prev.filter(s => s !== pill.value));
  };

  const pills = [
    { type: 'curriculum', label: region === 'US' ? '🇺🇸 US Common Core' : '🇬🇧 UK Curriculum', removable: false },
    difficulty ? { type: 'difficulty', label: difficulty, removable: true } : null,
    ...selectedSubjects.map(s => ({ type: 'subject', label: s, value: s, removable: true })),
  ].filter(Boolean);

  return (
    <div className={`custom-prompt${compact ? ' custom-prompt--compact' : ''}`}>
      {showSuggestions && (
        <div className="cp-suggestions">
          <span className="cp-suggestions-label">Try:</span>
          {suggestions.map((s, i) => (
            <button
              key={i}
              className="cp-suggestion-chip"
              type="button"
              onClick={() => { setUserText(s); if (error) setError(''); }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <textarea
        aria-label="Describe your worksheet"
        className={`cp-textarea${error ? ' cp-textarea--error' : ''}`}
        placeholder={`e.g. "10 multiplication questions for Year 4 with mixed difficulty"`}
        disabled={loading}
        maxLength={MAX_USER_PROMPT_CHARS}
        value={userText}
        onChange={(e) => { setUserText(e.target.value); if (error) setError(''); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleGenerate(); }
        }}
      />

      {error && <p className="cp-error">{error}</p>}

      {pills.length > 0 && (
        <div className="cp-pills">
          {pills.map((pill, i) => (
            <span key={i} className={`cp-pill cp-pill--${pill.type}`}>
              {pill.label}
              {pill.removable && (
                <button
                  className="cp-pill-remove"
                  onClick={() => handleRemovePill(pill)}
                  aria-label={`Remove ${pill.label}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="cp-class-settings">
        <label>Year / grade<input value={settings.yearGroup || ''} maxLength={60} disabled={loading} placeholder="e.g. Year 4 or Grade 3" onChange={e => changeSetting('yearGroup', e.target.value)} /></label>
        <SettingsSelect label="Curriculum" value={region} disabled={loading} onChange={setRegion} options={[["UK", "🇬🇧 UK National Curriculum"], ["US", "🇺🇸 US Common Core"]]} />
        <SettingsSelect label="Language" value={settings.language} disabled={loading} onChange={value => changeSetting('language', value)} options={[["auto", "Use prompt language"], ...['English','Spanish','French','Arabic','Tamil','Sinhala'].map(language => [language, language])]} />
        <SettingsSelect label="Reading level" value={settings.readingLevel} disabled={loading} onChange={value => changeSetting('readingLevel', value)} options={[["match", "Match year / grade"], ["simple", "Simpler wording"], ["adult", "Adult learners"]]} />
        <div className="cp-topic-control"><span>Topics</span><button className="cp-topics-btn" type="button" disabled={loading} onClick={() => setSubjectsOpen(true)}>{selectedSubjects.length ? `${selectedSubjects.length} selected` : '+ Topics'}</button></div>
      </div>
      <p className="cp-setting-help">Reading level changes the wording. Describe the maths difficulty in your prompt. Include the number of questions you need in your prompt.</p>
      <fieldset className="cp-style-picker" disabled={loading}>
        <legend>Worksheet style</legend>
        <p>Choose the printed layout. Examples show different lessons; every style works with any maths topic.</p>
        <div className="cp-style-options">
          {[['clean','Clean Classroom','Clear numbering and balanced space.'],['exam','Exam Practice','Formal structure with room for working.'],['guided','Guided Practice','Larger text and more breathing room.']].map(([value,label,description]) => <label key={value} className={`cp-style-card ${settings.style === value ? 'is-selected' : ''}`}>
            <input type="radio" name={styleGroupId} value={value} checked={settings.style === value} onChange={() => changeSetting('style', value)} />
            <WorksheetStylePreview style={value} />
            <strong>{label}</strong><span>{description}</span>
          </label>)}
        </div>
      </fieldset>

      <div className="cp-params">
        <button
          className="cp-generate-btn"
          onClick={handleGenerate}
          disabled={quotaBlocked || !userText.trim() || loading}
        >
          Generate →
        </button>
      </div>

      <SubjectsLibrary
        open={subjectsOpen}
        selectedSubjects={selectedSubjects}
        onSelect={setSelectedSubjects}
        onClose={() => setSubjectsOpen(false)}
        region={region}
      />
    </div>
  );
}
